import React, { FC, ReactNode, useCallback } from 'react';
import { Button, ListGroup } from 'reactstrap';
import { ActionType, store, Theme } from '../services';
import { useTranslation } from '../translations';
import {
  DateRange,
  useConfig,
  useConvention,
  usePreferences,
  useUser,
  useUserSafe,
} from '../utils';
import { UserAvatar } from '../utils/UserAvatar';
import { MaterialIcon, SidenavItem } from '.';

import './SideNav.scss';

const UserInfo: FC = () => {
  const { ts } = useTranslation();
  const config = useConfig();
  const convention = useConvention();
  const logout = useCallback(async () => {
    const result = await api.logout();

    if (result?.redirect_token) {
      const activeConvention = config.conventions.find((c) => c.id === config.activeConventionId);

      if (activeConvention) {
        window.location.replace(
          `${location.protocol}//${activeConvention.domain}/oauth/logout?redirect_token=${result.redirect_token}&conventionId=${convention.id}`,
        );

        return;
      }
    }

    store.dispatch({ type: ActionType.Logout });
  }, [config.activeConventionId, config.conventions, convention.id]);

  const { preferredName, username, profilePictureUrl, id } = useUserSafe();
  return (
    <div className="user-info">
      <div className="avatar">
        <UserAvatar size="small" url={profilePictureUrl} />
      </div>
      <div
        className="greeting"
        title={username ? `ID: #${id}\nUsername: ${username}` : `ID: #${id}`}
      >
        <p>{ts('common.welcome_back')},</p>
        <h4>{preferredName ?? username}</h4>
      </div>
      <div className="clearfix" />
      <div className="p-3">
        <Button block color="secondary" id="logout" onClick={logout}>
          {ts('logout')}
        </Button>
      </div>
    </div>
  );
};

export const Sidenav: FC<{ readonly children?: ReactNode }> = ({ children }) => {
  const user = useUser();
  const preferences = usePreferences();

  const toggleSidenav = useCallback(() => {
    store.dispatch({
      type: ActionType.SideNavToggle,
    });
  }, []);

  const switchTheme = useCallback(
    (theme: Theme) => {
      store.dispatch({
        preferences: {
          ...preferences,
          theme,
        },
        type: ActionType.PreferenceUpdate,
      });
    },
    [preferences],
  );

  const lightTheme = useCallback(() => {
    switchTheme(Theme.Light);
  }, [switchTheme]);

  const darkTheme = useCallback(() => {
    switchTheme(Theme.Dark);
  }, [switchTheme]);

  return (
    <div className="sidenav" id="sidenav">
      <Button className="toggle-btn" color="primary" onClick={toggleSidenav}>
        <MaterialIcon name="close" />
      </Button>
      <ConventionInfo />
      {user ? <UserInfo /> : null}
      <ListGroup flush>
        {children}
        <SidenavItem header requireLoggedIn text="theme_settings" />
        <div className="theme-switcher">
          <div className="light" id="themeLight" onClick={lightTheme} />
          <div className="dark" id="themeDark" onClick={darkTheme} />
        </div>
      </ListGroup>
    </div>
  );
};

const ConventionInfo: FC = () => {
  const { longName, startAt, endAt, timeZone } = useConvention();
  return (
    <div className="event-info">
      <h4>{longName}</h4>
      <p id="conventionDateRange">
        <DateRange end={endAt} start={startAt} timeZone={timeZone} />
      </p>
    </div>
  );
};
